import * as XLSX from "xlsx-js-style";
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from "@capacitor/core";
import { FileOpener } from "@capacitor-community/file-opener";

const EXCEL_CONTENT_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const getFileName = (name: string) => {
  let sheetName = name || "ExportResult";
  let fileName = `${sheetName}`;
  return {
    sheetName,
    fileName
  };
};

const SHEET_HEADER_COLORS = [
  "1A7A6A", "2E5E8C", "8C4A7A", "B06A2E",
  "4A7A3A", "9C3B3B", "5A5A8C", "7A6A2E"
];

const lightenColor = (hex: string, amount: number) => {
  let lightened = "";
  for(let start = 0; start < 6; start += 2){
    const channel = parseInt(hex.substring(start, start + 2), 16);
    const mixed = Math.round(channel + (255 - channel) * amount);
    lightened += mixed.toString(16).toUpperCase().padStart(2, "0");
  }
  return lightened;
};

export class TableUtil {
  static exportArrayToExcel(arr: any[], name?: any) {
    let { sheetName, fileName } = getFileName(name);
    var wb = XLSX.utils.book_new();
    var ws = XLSX.utils.json_to_sheet(arr);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    if(Capacitor.getPlatform() != 'android'){
      XLSX.writeFile(wb, `${fileName}.xlsx`);
    }else if(Capacitor.getPlatform() == 'android'){
      const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const fileBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });  
      this.saveExcelFile(fileBlob, `${fileName}.xlsx`);
    }
  }

  static async saveExcelFile(blob: Blob, fileName: string) {
    try{
      const base64Data = await this.blobToBase64(blob);
      let savedFile;
      try{
        // Directory.External is the app's own folder on the external storage. It needs no
        // permission on any Android version, unlike the shared storage root that scoped
        // storage blocks from Android 11 onwards - that is why the export silently did nothing.
        savedFile = await Filesystem.writeFile({
          path: fileName,
          data: base64Data,
          directory: Directory.External,
          recursive: true
        });
      }catch(externalStorageError){
        // no external storage mounted on this device - the internal cache always exists
        savedFile = await Filesystem.writeFile({
          path: fileName,
          data: base64Data,
          directory: Directory.Cache,
          recursive: true
        });
      }
      await this.openFile(savedFile.uri);
    }catch(error){
      // this used to be an unhandled rejection inside the FileReader callback, so a failed
      // export left the user with no file and no message at all
      console.error(`Excel export failed for ${fileName}`, error);
    }
  }

  private static blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onloadend = () => {
        const dataUrl = reader.result ? reader.result.toString() : "";
        resolve(dataUrl.split(',')[1]);
      };
      reader.readAsDataURL(blob);
    });
  }

  static async openFile(filePath: string) {
    await FileOpener.open({
      filePath: filePath,
      // Android derives the type from the file name, and that lookup drops names with
      // spaces or Hebrew letters - without this the chooser opens with no app to handle it
      contentType: EXCEL_CONTENT_TYPE,
      openWithDefault: false // Forces app chooser
    });
  }

  static applyHeaderAndBodyStyles(ws: any, sheetIndex: number = 0, tableHeaderRow: number = 0) {
    if(!ws || !ws['!ref']){
      return;
    }
    const headerColor = SHEET_HEADER_COLORS[sheetIndex % SHEET_HEADER_COLORS.length];
    const bandColor = lightenColor(headerColor, 0.85);
    const titleStyle = {
      font: { name: "Arial", sz: 13, bold: true, color: { rgb: headerColor } },
      alignment: { horizontal: "center", vertical: "center" }
    };
    const subtitleStyle = {
      font: { name: "Arial", sz: 10, bold: true, color: { rgb: headerColor } },
      alignment: { horizontal: "center", vertical: "center" }
    };
    const headerStyle = {
      fill: { fgColor: { rgb: headerColor } },
      font: { name: "Arial", sz: 11, bold: true, color: { rgb: "FFFFFF" } }
    };
    const bandedRowStyle = {
      fill: { fgColor: { rgb: bandColor } },
      font: { name: "Arial", sz: 10, bold: false, color: { rgb: "000000" } }
    };
    const plainRowStyle = {
      font: { name: "Arial", sz: 10, bold: false, color: { rgb: "000000" } }
    };
    const range = XLSX.utils.decode_range(ws['!ref']);
    for(let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++){
      const isTitleRow = rowIndex < tableHeaderRow;
      const isHeaderRow = rowIndex == tableHeaderRow;
      const isBandedRow = rowIndex > tableHeaderRow && ((rowIndex - tableHeaderRow) % 2 == 1);
      for(let colIndex = range.s.c; colIndex <= range.e.c; colIndex++){
        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        if(!ws[cellAddress]){
          continue;
        }
        if(isTitleRow){
          ws[cellAddress].s = rowIndex == range.s.r ? titleStyle : subtitleStyle;
        }else if(isHeaderRow){
          ws[cellAddress].s = headerStyle;
        }else{
          ws[cellAddress].s = isBandedRow ? bandedRowStyle : plainRowStyle;
        }
      }
    }
  }

  static exportSheetsToExcel(sheets: { name: string, rows: any[] }[], name?: any, subtitle?: string) {
    let { sheetName, fileName } = getFileName(name);
    var wb = XLSX.utils.book_new();
    let usedSheetNames: string[] = [];
    (sheets || []).forEach((sheet, index) => {
      // Excel refuses sheet names longer than 31 chars or containing : \ / ? * [ ]
      let safeName = (sheet.name || `${sheetName}${index + 1}`).replace(/[:\\\/\?\*\[\]]/g, ' ').trim().substring(0, 31);
      if(safeName == ''){
        safeName = `${sheetName}${index + 1}`.substring(0, 31);
      }
      if(usedSheetNames.indexOf(safeName) > -1){
        safeName = `${safeName.substring(0, 28)}_${index + 1}`;
      }
      usedSheetNames.push(safeName);
      // the event type - and the selected date range, when there is one - sit above
      // the table, then one empty row, then the table itself
      const titleRows: any[][] = [[sheet.name || safeName]];
      if(subtitle){
        titleRows.push([subtitle]);
      }
      const tableHeaderRow = titleRows.length + 1;
      var ws = XLSX.utils.aoa_to_sheet(titleRows);
      XLSX.utils.sheet_add_json(ws, sheet.rows || [], { origin: `A${tableHeaderRow + 1}` });
      // each title row is one cell spanning the full table width, not a cell per column
      const sheetRange = XLSX.utils.decode_range(ws['!ref'] || "A1");
      if(sheetRange.e.c > sheetRange.s.c){
        const merges = ws['!merges'] || [];
        for(let titleRowIndex = 0; titleRowIndex < titleRows.length; titleRowIndex++){
          merges.push({
            s: { r: titleRowIndex, c: sheetRange.s.c },
            e: { r: titleRowIndex, c: sheetRange.e.c }
          });
        }
        ws['!merges'] = merges;
      }
      this.applyHeaderAndBodyStyles(ws, index, tableHeaderRow);
      XLSX.utils.book_append_sheet(wb, ws, safeName);
    });
    if(usedSheetNames.length == 0){
      // a workbook with no sheet at all cannot be written
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([]), sheetName);
    }
    if(Capacitor.getPlatform() != 'android'){
      XLSX.writeFile(wb, `${fileName}.xlsx`);
    }else if(Capacitor.getPlatform() == 'android'){
      const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const fileBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      this.saveExcelFile(fileBlob, `${fileName}.xlsx`);
    }
  }

  static exportArrayToExcelWithFormatingCells(arr: any[], name?: string) {
    let { sheetName, fileName } = getFileName(name);
    var wb = XLSX.utils.book_new();
    var ws = XLSX.utils.json_to_sheet(arr);
    for(var R = 0; R <= arr.length+1; R++) {
      var addr = XLSX.utils.encode_cell({r:R, c:13});
      if(!ws[addr]) continue;
      ws[addr].z = "#,##0"; // the actual number format, "0.00" for two decimal places, "#,##0.00" for thousands, etc.
    }
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    if(Capacitor.getPlatform() != 'android'){
      XLSX.writeFile(wb, `${fileName}.xlsx`);
    }else if(Capacitor.getPlatform() == 'android'){
      const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const fileBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });  
      this.saveExcelFile(fileBlob, `${fileName}.xlsx`);
    }
  }
}
